import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./components/ui/card";

interface SummarySectionProps {
  totalIncome: number;
  totalMortgageCost: number;
  totalEssentialExpenses: number;
  totalNonEssentialExpenses: number;
}

export function SummarySection({
  totalIncome,
  totalMortgageCost,
  totalEssentialExpenses,
  totalNonEssentialExpenses,
}: SummarySectionProps) {
  const totalSpending =
    totalMortgageCost + totalEssentialExpenses + totalNonEssentialExpenses;
  const totalSaving = totalIncome - totalSpending;
  const totalSpendingPercentage = (totalSpending * 100) / totalIncome;
  const totalSavingPercentage = (totalSaving * 100) / totalIncome;

  return (
    <div className="space-y-8 px-4 lg:px-6">
      {/* Financial Overview - Top KPI Cards */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Financial Overview</h2>
          <div className="text-sm text-muted-foreground">
            Annual breakdown
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Income Card - Standalone for emphasis */}
          <Card className="@container/card *:data-[slot=card]:shadow-xs *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:from-green-50 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card border-green-200 dark:border-green-800">
            <CardHeader className="relative">
              <CardDescription className="text-green-700 dark:text-green-400">Total Annual Income</CardDescription>
              <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums text-green-900 dark:text-green-100">
                ${+(totalIncome / 52).toFixed(2)}/wk
              </CardTitle>
            </CardHeader>
            <CardFooter className="flex-col items-start gap-1 text-sm">
              <div className="text-muted-foreground">
                ${+(totalIncome / 26).toFixed(2)} per fortnight
              </div>
              <div className="text-muted-foreground">
                ${+(totalIncome / 12).toFixed(2)} per month
              </div>
              <div className="text-xs text-muted-foreground/80 mt-1">
                ${totalIncome.toLocaleString()} annually
              </div>
            </CardFooter>
          </Card>

          {/* Savings Card - Prominent placement */}
          <Card className="@container/card *:data-[slot=card]:shadow-xs *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:from-blue-50 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card border-blue-200 dark:border-blue-800">
            <CardHeader className="relative">
              <CardDescription className="text-blue-700 dark:text-blue-400">Savings Rate (% of Income)</CardDescription>
              <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums text-blue-900 dark:text-blue-100">
                {totalSavingPercentage.toFixed(1)}%
              </CardTitle>
            </CardHeader>
            <CardFooter className="flex-col items-start gap-1 text-sm">
              <div className="text-muted-foreground">
                ${(totalSaving / 52).toFixed(2)} per week
              </div>
              <div className="text-muted-foreground">
                ${(totalSaving / 26).toFixed(2)} per fortnight
              </div>
              <div className="text-muted-foreground">
                ${(totalSaving / 12).toFixed(2)} per month
              </div>
              <div className="text-xs text-muted-foreground/80 mt-1">
                Recommended: ≥20% of income
              </div>
            </CardFooter>
          </Card>

          {/* Total Spending Card */}
          <Card className="@container/card *:data-[slot=card]:shadow-xs *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:from-red-50 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card border-red-200 dark:border-red-800">
            <CardHeader className="relative">
              <CardDescription className="text-red-700 dark:text-red-400">Total Outgoings (% of Income)</CardDescription>
              <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums text-red-900 dark:text-red-100">
                {totalSpendingPercentage.toFixed(1)}%
              </CardTitle>
            </CardHeader>
            <CardFooter className="flex-col items-start gap-1 text-sm">
              <div className="text-muted-foreground">
                ${(totalSpending / 52).toFixed(2)} per week
              </div>
              <div className="text-muted-foreground">
                ${(totalSpending / 26).toFixed(2)} per fortnight
              </div>
              <div className="text-muted-foreground">
                ${(totalSpending / 12).toFixed(2)} per month
              </div>
              <div className="text-xs text-muted-foreground/80 mt-1">
                All expenses combined
              </div>
            </CardFooter>
          </Card>

          {/* Net Position Card */}
          <Card className={`@container/card *:data-[slot=card]:shadow-xs *:data-[slot=card]:bg-gradient-to-t dark:*:data-[slot=card]:bg-card ${
            totalSaving >= 0
              ? '*:data-[slot=card]:from-emerald-50 *:data-[slot=card]:to-card border-emerald-200 dark:border-emerald-800'
              : '*:data-[slot=card]:from-red-50 *:data-[slot=card]:to-card border-red-200 dark:border-red-800'
          }`}>
            <CardHeader className="relative">
              <CardDescription className={totalSaving >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}>
                Monthly Position
              </CardDescription>
              <CardTitle className={`@[250px]/card:text-3xl text-2xl font-semibold tabular-nums ${
                totalSaving >= 0
                  ? "text-emerald-900 dark:text-emerald-100"
                  : "text-red-900 dark:text-red-100"
              }`}>
                {totalSaving >= 0 ? '+' : ''}${(totalSaving / 12).toFixed(2)}
              </CardTitle>
            </CardHeader>
            <CardFooter className="flex-col items-start gap-1 text-sm">
              <div className="text-muted-foreground">
                {totalSaving >= 0 ? '+' : ''}${(totalSaving / 52).toFixed(2)} per week
              </div>
              <div className="text-muted-foreground">
                {totalSaving >= 0 ? '+' : ''}${(totalSaving / 26).toFixed(2)} per fortnight
              </div>
              <div className="text-xs text-muted-foreground/80 mt-1">
                {totalSaving >= 0 ? 'Building wealth' : 'Spending more than earning'}
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>

      {/* Expense Breakdown Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-foreground">Expense Breakdown</h3>
          <div className="text-sm text-muted-foreground">
            Percentage of income
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Housing Costs */}
          {totalMortgageCost > 0 && (
            <Card className="@container/card *:data-[slot=card]:shadow-xs *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:from-purple-50 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card border-purple-200 dark:border-purple-800">
              <CardHeader className="relative">
                <CardDescription className="text-purple-700 dark:text-purple-400">Housing Costs (% of Income)</CardDescription>
                <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums text-purple-900 dark:text-purple-100">
                  {+((totalMortgageCost * 100) / totalIncome).toFixed(1)}%
                </CardTitle>
              </CardHeader>
              <CardFooter className="flex-col items-start gap-1 text-sm">
                <div className="text-muted-foreground">
                  ${+(totalMortgageCost / 52).toFixed(2)} per week
                </div>
                <div className="text-muted-foreground">
                  ${+(totalMortgageCost / 26).toFixed(2)} per fortnight
                </div>
                <div className="text-muted-foreground">
                  ${+(totalMortgageCost / 12).toFixed(2)} per month
                </div>
                <div className="text-xs text-muted-foreground/80 mt-1">
                  Recommended: ≤30% of income
                </div>
              </CardFooter>
            </Card>
          )}

          {/* Essential Expenses */}
          <Card className="@container/card *:data-[slot=card]:shadow-xs *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:from-amber-50 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card border-amber-200 dark:border-amber-800">
            <CardHeader className="relative">
              <CardDescription className="text-amber-700 dark:text-amber-400">Essential Expenses (% of Income)</CardDescription>
              <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums text-amber-900 dark:text-amber-100">
                {+((totalEssentialExpenses * 100) / totalIncome).toFixed(1)}%
              </CardTitle>
            </CardHeader>
            <CardFooter className="flex-col items-start gap-1 text-sm">
              <div className="text-muted-foreground">
                ${+(totalEssentialExpenses / 52).toFixed(2)} per week
              </div>
              <div className="text-muted-foreground">
                ${+(totalEssentialExpenses / 26).toFixed(2)} per fortnight
              </div>
              <div className="text-muted-foreground">
                ${+(totalEssentialExpenses / 12).toFixed(2)} per month
              </div>
              <div className="text-xs text-muted-foreground/80 mt-1">
                Needs: groceries, utilities, transport
              </div>
            </CardFooter>
          </Card>

          {/* Discretionary Spending */}
          <Card className="@container/card *:data-[slot=card]:shadow-xs *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:from-orange-50 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card border-orange-200 dark:border-orange-800">
            <CardHeader className="relative">
              <CardDescription className="text-orange-700 dark:text-orange-400">Discretionary Spending (% of Income)</CardDescription>
              <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums text-orange-900 dark:text-orange-100">
                {+((totalNonEssentialExpenses * 100) / totalIncome).toFixed(1)}%
              </CardTitle>
            </CardHeader>
            <CardFooter className="flex-col items-start gap-1 text-sm">
              <div className="text-muted-foreground">
                ${+(totalNonEssentialExpenses / 52).toFixed(2)} per week
              </div>
              <div className="text-muted-foreground">
                ${+(totalNonEssentialExpenses / 26).toFixed(2)} per fortnight
              </div>
              <div className="text-muted-foreground">
                ${+(totalNonEssentialExpenses / 12).toFixed(2)} per month
              </div>
              <div className="text-xs text-muted-foreground/80 mt-1">
                Wants: dining, entertainment, hobbies
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
}
